import { useMemo, useState } from "react";
import {
  HIGH_MAX,
  LOW_MAX_DEFAULT,
  MEDIUM_MAX_DEFAULT,
  HIGH_MIN_DEFAULT,
} from "../constants";

const getInitialFormData = () => ({
  budget: 1500,
  quantity: 1,
  categories: [],
  lowMax: LOW_MAX_DEFAULT,
  mediumMax: MEDIUM_MAX_DEFAULT,
  highMin: HIGH_MIN_DEFAULT,
  rangePercentage: { max: 10, min: 10 },
});

export const useGenerateKitForm = (errors, setErrors) => {
  const [formData, setFormData] = useState(getInitialFormData);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  const productSplit = useMemo(
    () => ({
      low: { min: 1, max: formData.lowMax },
      medium: { min: formData.lowMax + 1, max: formData.mediumMax },
      high: { min: formData.highMin, max: HIGH_MAX },
    }),
    [formData.lowMax, formData.mediumMax, formData.highMin],
  );

  const updateForm = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const updateLowMax = (value) => {
    const num = Math.max(1, Math.min(Number(value) || 1, HIGH_MAX - 2));
    setFormData((prev) => {
      const next = { ...prev, lowMax: num };
      if (next.mediumMax <= num + 1) next.mediumMax = num + 2;
      if (next.highMin <= next.mediumMax) next.highMin = next.mediumMax + 1;
      return next;
    });
  };

  const updateMediumMax = (value) => {
    const num = Number(value);
    if (isNaN(num)) return;
    setFormData((prev) => {
      const newMediumMax = Math.max(
        prev.lowMax + 2,
        Math.min(HIGH_MAX - 1, num),
      );
      const next = { ...prev, mediumMax: newMediumMax };
      if (prev.highMin <= newMediumMax) next.highMin = newMediumMax + 1;
      return next;
    });
  };

  const updateHighMin = (value) => {
    const num = Number(value);
    if (isNaN(num)) return;
    setFormData((prev) => ({
      ...prev,
      highMin: Math.max(prev.mediumMax + 1, Math.min(HIGH_MAX, num)),
    }));
  };

  const updateRangePercentage = (field, value) => {
    const num = Number(value);
    if (isNaN(num)) return;
    setFormData((prev) => ({
      ...prev,
      rangePercentage: {
        ...prev.rangePercentage,
        [field]: Math.max(0, Math.min(100, num)),
      },
    }));
  };

  const isCategorySelected = (catId) =>
    formData.categories.some((c) => c.categoryId === catId);

  const getSelectedSubIds = (catId) => {
    const entry = formData.categories.find((c) => c.categoryId === catId);
    return entry?.subCategoryIds || [];
  };

  const toggleCategoryExpand = (catId) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  };

  const toggleCategory = (category) => {
    const subCategories = category.subCategories || [];
    const allSubIds = subCategories.map((s) => s._id);

    setFormData((prev) => {
      const exists = prev.categories.find((c) => c.categoryId === category._id);
      if (exists) {
        return {
          ...prev,
          categories: prev.categories.filter(
            (c) => c.categoryId !== category._id,
          ),
        };
      }
      return {
        ...prev,
        categories: [
          ...prev.categories,
          { categoryId: category._id, subCategoryIds: [...allSubIds] },
        ],
      };
    });
    if (!expandedCategories.has(category._id)) {
      setExpandedCategories((prev) => new Set(prev).add(category._id));
    }
  };

  const toggleSubcategory = (catId, subId) => {
    setFormData((prev) => {
      const entry = prev.categories.find((c) => c.categoryId === catId);
      if (!entry) return prev;
      const has = entry.subCategoryIds.includes(subId);
      const nextSubIds = has
        ? entry.subCategoryIds.filter((id) => id !== subId)
        : [...entry.subCategoryIds, subId];
      return {
        ...prev,
        categories: prev.categories.map((c) =>
          c.categoryId === catId ? { ...c, subCategoryIds: nextSubIds } : c,
        ),
      };
    });
  };

  const BUDGET_MAX = 100000; // 1 lakh
  const QUANTITY_MAX = 50;

  const validateForm = () => {
    const newErrors = {};
    if (!formData.budget || formData.budget <= 0)
      newErrors.budget = "Budget must be greater than 0";
    if (formData.budget > BUDGET_MAX)
      newErrors.budget = `Budget cannot exceed ₹${BUDGET_MAX}`;
    if (formData.quantity != null && formData.quantity !== "" && (formData.quantity < 1 || formData.quantity > QUANTITY_MAX))
      newErrors.quantity = `Quantity must be between 1 and ${QUANTITY_MAX}`;

    const { rangePercentage } = formData;
    if (formData.lowMax >= formData.mediumMax)
      newErrors.productSplit = "Low max must be less than medium max";
    if (formData.mediumMax >= formData.highMin)
      newErrors.productSplit = "Medium max must be less than high min";
    if (rangePercentage.min > rangePercentage.max)
      newErrors.rangePercentage = "Min percentage cannot exceed max";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return {
    formData,
    productSplit,
    expandedCategories,
    updateForm,
    updateLowMax,
    updateMediumMax,
    updateHighMin,
    updateRangePercentage,
    isCategorySelected,
    getSelectedSubIds,
    toggleCategoryExpand,
    toggleCategory,
    toggleSubcategory,
    validateForm,
  };
};
